"use client"

import { useState, useMemo } from "react"
import { X, Search, Trophy, Phone, Mail, GraduationCap, Eye, CheckCircle2, Clock, ChevronLeft, ChevronRight } from "@/lib/icons"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { normalizeLeadStage } from "@/lib/lead-stages"
import { useEscapeKey } from "@/hooks/useEscapeKey"

const STATUS_META = {
    inscrito: { label: "Inscrito", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", icon: Trophy },
    cierre: { label: "Cierre de inscripción", color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300", icon: CheckCircle2 },
}

// Fecha efectiva de inscripción de un lead (con fallbacks razonables)
const getInscripcionDate = (lead) => {
    const raw = lead.inscripcion_confirmada_at || lead.fecha_inscripcion || lead.updated_at || lead.created_at
    const d = raw ? new Date(raw) : null
    return d && !isNaN(d.getTime()) ? d : null
}

export function InscritosModal({ open, onClose, leads = [], onViewDetails }) {
    const [searchTerm, setSearchTerm] = useState("")
    const [statusTab, setStatusTab] = useState("todos")
    // Ciclo mensual: cada mes arranca en cero; se navega a meses anteriores
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date()
        return new Date(now.getFullYear(), now.getMonth(), 1)
    })

    useEscapeKey(open, onClose)

    const isCurrentMonth = useMemo(() => {
        const now = new Date()
        return selectedMonth.getFullYear() === now.getFullYear() && selectedMonth.getMonth() === now.getMonth()
    }, [selectedMonth])

    const monthLabel = selectedMonth.toLocaleDateString("es-MX", { month: "long", year: "numeric" })

    // Leads inscritos/cierre DEL MES seleccionado (por fecha de inscripción)
    const monthLeads = useMemo(() => {
        const start = selectedMonth
        const end = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1)
        return leads.filter((lead) => {
            const d = getInscripcionDate(lead)
            return d && d >= start && d < end
        })
    }, [leads, selectedMonth])

    const filteredLeads = useMemo(() => {
        let result = [...monthLeads]
        if (statusTab !== "todos") {
            result = result.filter(lead => normalizeLeadStage(lead.estado_lead) === statusTab)
        }
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase()
            result = result.filter(lead =>
                (lead.nombre_tutor || "").toLowerCase().includes(term) ||
                (lead.telefono || "").includes(term) ||
                (lead.email || "").toLowerCase().includes(term)
            )
        }
        return result.sort((a, b) => (getInscripcionDate(b) || 0) - (getInscripcionDate(a) || 0))
    }, [monthLeads, searchTerm, statusTab])

    const inscritosCount = useMemo(() => monthLeads.filter(l => normalizeLeadStage(l.estado_lead) === "inscrito").length, [monthLeads])
    const cierreCount = useMemo(() => monthLeads.filter(l => normalizeLeadStage(l.estado_lead) === "cierre").length, [monthLeads])

    const shiftMonth = (delta) => setSelectedMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1))

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:p-6 pb-20">
            <div
                className="relative w-full max-w-4xl rounded-2xl bg-white shadow-2xl dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
                role="dialog"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-100 p-4 sm:p-5 dark:border-slate-800 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400">
                            <Trophy className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">Inscritos y Cierre</h2>
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                {monthLeads.length} en <span className="capitalize font-semibold">{monthLabel}</span> — cada mes inicia un ciclo nuevo
                            </p>
                        </div>
                    </div>
                    {/* Navegador de mes */}
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => shiftMonth(-1)}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 transition-colors"
                            title="Mes anterior"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="text-xs font-bold capitalize text-slate-700 dark:text-slate-200 min-w-[110px] text-center tabular-nums">
                            {monthLabel}
                        </span>
                        <button
                            onClick={() => shiftMonth(1)}
                            disabled={isCurrentMonth}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Mes siguiente"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        {!isCurrentMonth && (
                            <button
                                onClick={() => { const now = new Date(); setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1)) }}
                                className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline px-1"
                            >
                                Mes actual
                            </button>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-5 space-y-4">
                    {/* Tabs + Search */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        {/* Status tabs */}
                        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
                            {[
                                { key: "todos", label: "Todos", count: monthLeads.length },
                                { key: "cierre", label: "Cierre", count: cierreCount },
                                { key: "inscrito", label: "Inscritos", count: inscritosCount },
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setStatusTab(tab.key)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${statusTab === tab.key
                                        ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                                        : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                                        }`}
                                >
                                    {tab.label}
                                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${statusTab === tab.key
                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                                        : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                                        }`}>
                                        {tab.count}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Buscar por nombre, teléfono o email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Leads list — agrupados por mes */}
                    <div className="max-h-[60vh] overflow-y-auto space-y-1">
                        {filteredLeads.length === 0 ? (
                            <div className="py-12 text-center text-slate-500 dark:text-slate-400">
                                <Trophy className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                Sin inscritos en <span className="capitalize font-semibold">{monthLabel}</span>.
                                <p className="text-xs mt-1 text-slate-400">Usa las flechas del encabezado para ver meses anteriores.</p>
                            </div>
                        ) : (
                            <div className="grid gap-2 py-2">
                                {filteredLeads.map((lead) => {
                                                    const stage = normalizeLeadStage(lead.estado_lead)
                                                    const meta = STATUS_META[stage] || STATUS_META.cierre
                                                    const StatusIcon = meta.icon
                                                    const asesor = lead.asesor_nombre || lead.asesor_email || ""
                                                    const asesorFirst = asesor ? asesor.split(" ")[0] : null
                                                    const inscDate = lead.inscripcion_confirmada_at || lead.fecha_inscripcion || lead.updated_at || lead.created_at

                                                    return (
                                                        <div key={lead.id} className="group relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-800/30 p-3 transition-all hover:border-slate-200 hover:shadow-sm dark:hover:border-slate-700">
                                                            <div className="flex items-start gap-3 w-full sm:w-auto">
                                                                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.color}`}>
                                                                    <StatusIcon className="h-4 w-4" />
                                                                </div>
                                                                <div className="space-y-0.5 min-w-0 flex-1">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                                                                            {lead.nombre_tutor || "Sin nombre"}
                                                                        </h4>
                                                                        <span className={`inline-flex py-0.5 px-2 rounded-md text-[10px] font-bold ${meta.color}`}>
                                                                            {meta.label}
                                                                        </span>
                                                                        {lead.nivel_educativo && (
                                                                            <span className="inline-flex py-0.5 px-2 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                                                                {lead.nivel_educativo}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                                                                        {lead.telefono && (
                                                                            <span className="flex items-center gap-1">
                                                                                <Phone className="h-3 w-3" />
                                                                                {lead.telefono}
                                                                            </span>
                                                                        )}
                                                                        {lead.email && (
                                                                            <span className="flex items-center gap-1 break-all">
                                                                                <Mail className="h-3 w-3" />
                                                                                {lead.email}
                                                                            </span>
                                                                        )}
                                                                        {inscDate && (
                                                                            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                                                                <Clock className="h-3 w-3" />
                                                                                {new Date(inscDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                            </span>
                                                                        )}
                                                                        {asesorFirst && (
                                                                            <span className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-medium">
                                                                                <GraduationCap className="h-3 w-3" />
                                                                                por {asesorFirst}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => onViewDetails(lead)}
                                                                className="w-full sm:w-auto text-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                                                            >
                                                                <Eye className="mr-1.5 h-3.5 w-3.5" />
                                                                Ver
                                                            </Button>
                                                        </div>
                                                    )
                                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
